import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { Flex } from 'foundations-components/components/Flex';
import { FrameworkDataTable } from 'framework-data-table';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import ForecastManualSubmissionConfig from 'ui-coaching-tips-shared-config/config/forecastManualSubmissionConfig';
import UICoachingTips from 'ui-coaching-tips/UICoachingTips';
import { fetchDeals, setDealTablePagination, setDealTableSort } from '../../actions/DealsActions';
import { SUCCEEDED } from '../../constants/RequestStatusTypes';
import { getDealsFetchError, getDealsArray, getDealsStatus, getDealsTotal, getDealColumnWidths, getDealTablePagination, getDealTableSort, getIsDefaultSort } from '../../selectors/dealsSelectors';
import { getGates, getIsUngatedToForecastingMultipleOutcomes, getIsUngatedToManualSubmissionOverhaul, getScopes } from '../../selectors/authSelectors';
import { getProperties } from '../../selectors/properties';
import { getIsDealStageView, getRepDealColumnsStatus, getRepDealColumnsError, getRepDealColumnsForSelectedForecastType, getSelectedForecastTypePropertiesOrGatedDefault } from '../../selectors/forecastSelectors';
import { convertDealPropertiesForFrameworkDataTable, convertPaginationStateForApi, convertSortStateForApi } from '../../utils/deals';
import { trackDealTableSort } from '../../utils/usageTracker';
import EditColumnsButton from '../columns/EditColumnsButton';
import { mapColumns } from '../frameworkDealTableCells/ColumnToCellMap';
import SaveBar from './SaveBar';
import DealTableEmptyState from './DealTableEmptyState';
import BulkActions from './BulkActions';
import BulkActions_FMOGated from './BulkActions_FMOGated';
import { fetchRepDealColumns } from '../../actions/ForecastActions';
import { SET_DEAL_COLUMN_WIDTH } from '../../actions/ActionTypes';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FrameworkDealTable = ({
  owner
}) => {
  var _owner$get;
  const dispatch = useDispatch();
  const deals = useSelector(getDealsArray);
  const dealsTotal = useSelector(getDealsTotal);
  const paginationState = useSelector(getDealTablePagination);
  const sortState = useSelector(getDealTableSort);
  const isDefaultSort = useSelector(getIsDefaultSort);
  const ownerId = owner === null || owner === void 0 || (_owner$get = owner.get) === null || _owner$get === void 0 ? void 0 : _owner$get.call(owner, 'ownerId');
  const dealsFetchLoading = useSelector(getDealsStatus) !== SUCCEEDED;
  const hasDealsFetchError = useSelector(getDealsFetchError);
  const repDealColumnsLoading = useSelector(getRepDealColumnsStatus) !== SUCCEEDED;
  const repDealColumnsError = useSelector(getRepDealColumnsError);
  const isDealStageView = useSelector(getIsDealStageView);
  const isUngatedToFMO = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  const forecastTypeProperties = useSelector(getSelectedForecastTypePropertiesOrGatedDefault);
  const forecastAmountProperty = forecastTypeProperties.amountProperty;
  const isUngatedToManualSubmissionOverhaul = useSelector(getIsUngatedToManualSubmissionOverhaul);
  const repDealColumns = useSelector(getRepDealColumnsForSelectedForecastType);
  const columnWidths = useSelector(getDealColumnWidths);
  const [hasDealsFetchCompletedOnce, setHasDealsFetchCompletedOnce] = useState(false);
  useEffect(() => {
    if (!dealsFetchLoading) {
      setHasDealsFetchCompletedOnce(true);
    }
  }, [dealsFetchLoading]);
  useEffect(() => {
    if (ownerId) {
      dispatch(fetchDeals(`${ownerId}`, Object.assign({}, convertPaginationStateForApi(paginationState), {
        sorts: convertSortStateForApi(sortState, isDefaultSort)
      }))).catch(() => {
        // should be caught in fetchDealData
      });
      dispatch(fetchRepDealColumns()).catch(() => {
        // should be caught in fetchRepDealColumns
      });
    }
  }, [sortState, paginationState, dispatch, ownerId, isDefaultSort]);

  // Removing for Manual Submission Part 1 - don't set selected deals from owner submission
  // Sets the selected deals from the owner submission when submission parameters change (new fetch, regardless of existence)
  // useEffect(() => {
  //   if (!submissionsAreFetching) {
  //     dispatch({
  //       type: SET_SELECTED_DEAL_IDS,
  //       payload: ownerSubmissions?.get('dealIds'),
  //     });
  //   }
  // }, [dispatch, ownerSubmissions, submissionsAreFetching]);

  const handleColumnWidthChange = ({
    columnName,
    nextWidth
  }) => {
    dispatch({
      type: SET_DEAL_COLUMN_WIDTH,
      payload: {
        columnName,
        width: nextWidth
      }
    });
  };
  const handlePageIndexChange = ({
    nextPageIndex
  }) => {
    dispatch(setDealTablePagination(Object.assign({}, paginationState, {
      pageIndex: nextPageIndex
    })));
  };
  const handlePageSizeChange = ({
    nextPageSize
  }) => {
    dispatch(setDealTablePagination({
      pageIndex: 0,
      pageSize: nextPageSize
    }));
  };
  const handleSortsChange = ({
    nextSorts
  }) => {
    const nextSort = nextSorts[0];
    dispatch(setDealTableSort(nextSort, false));
    trackDealTableSort(isDealStageView);
  };
  const propertyDefinitions = useSelector(getProperties);
  const columns = useMemo(() => {
    return mapColumns(repDealColumns, propertyDefinitions, columnWidths, isUngatedToManualSubmissionOverhaul, forecastAmountProperty);
  }, [repDealColumns, propertyDefinitions, columnWidths, isUngatedToManualSubmissionOverhaul, forecastAmountProperty]);
  const correctedDeals = useMemo(() => convertDealPropertiesForFrameworkDataTable(deals), [deals]);
  const bulkActions = useMemo(() => {
    if (isUngatedToManualSubmissionOverhaul) {
      return undefined;
    }
    if (isUngatedToFMO) {
      return BulkActions;
    }
    return BulkActions_FMOGated;
  }, [isUngatedToFMO, isUngatedToManualSubmissionOverhaul]);

  // NOTE: gates and scopes selectors need to be typed
  const gates = useSelector(getGates);
  const scopes = useSelector(getScopes);
  const hasGate = useCallback(gate => gates.includes(gate), [gates]);
  const hasScope = useCallback(scope => scopes.includes(scope), [scopes]);

  // Memoization avoids re-calling the coaching tips API when FrameworkDealTable re-renders
  const ManualSubmissionCoachingTipAnchor = useMemo(() => () => {
    if (!isUngatedToManualSubmissionOverhaul || !(correctedDeals !== null && correctedDeals !== void 0 && correctedDeals.length)) {
      return null;
    }

    // Coaching tips cannot be anchored in the header of the FDT
    // A workaround is to use Flex with a margin to place the anchor correctly
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UICoachingTips, {
        config: ForecastManualSubmissionConfig
      }), /*#__PURE__*/_jsx(Flex, {
        "data-coaching-tip": "forecast-submission-deal-selector",
        marginBlockStart: "800"
      })]
    });
  }, [isUngatedToManualSubmissionOverhaul, correctedDeals === null || correctedDeals === void 0 ? void 0 : correctedDeals.length]);
  if (hasDealsFetchError || repDealColumnsError) {
    // A red alert will show the Deals Fetch error. Permanent error UI to follow
    // Migrate to LoadingSpinner when centered issue is fixed
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "DEAL_TABLE_LOAD_FAIL",
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true
      })
    });
  }
  if (!hasDealsFetchCompletedOnce) {
    // Migrate to LoadingSpinner when centered issue is fixed
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "DEAL_TABLE_LOADED",
    children: /*#__PURE__*/_jsxs("div", {
      "data-fs-exclude": true,
      "data-test-id": "deal-table",
      children: [/*#__PURE__*/_jsx(Flex, {
        marginBlockEnd: "800",
        children: /*#__PURE__*/_jsx(FrameworkDataTable, {
          BulkActionsComponent: bulkActions,
          columns: columns,
          getRowId: row => `${row.id}`,
          hasGate: hasGate,
          hasScope: hasScope,
          headerLeftSlot: /*#__PURE__*/_jsx(ManualSubmissionCoachingTipAnchor, {}),
          headerRightSlot: /*#__PURE__*/_jsx(View, {
            marginInlineEnd: "400",
            as: "div",
            children: /*#__PURE__*/_jsx(EditColumnsButton, {})
          }),
          loading: dealsFetchLoading || repDealColumnsLoading,
          noRowsSlot: /*#__PURE__*/_jsx(DealTableEmptyState, {}),
          onColumnWidthChange: handleColumnWidthChange,
          onPageIndexChange: handlePageIndexChange,
          onPageSizeChange: handlePageSizeChange,
          onSortsChange: handleSortsChange,
          pageIndex: paginationState.pageIndex,
          pageSize: paginationState.pageSize,
          rows: correctedDeals,
          selectable: !isUngatedToManualSubmissionOverhaul,
          sorts: [sortState],
          totalRowCount: dealsTotal
        })
      }), /*#__PURE__*/_jsx(SaveBar, {
        ownerId: `${ownerId}`
      })]
    })
  });
};
export default FrameworkDealTable;